import { levelLabel } from '../../lib/format'

/** Exact canonical application text only. Never apply to stored rules or user-input fields. */
const TEXT = new Map<string, string>([
  ['Motor-reducer diagnosis (13 criteria)', 'Diagnóstico de motorreductor (13 criterios)'],
  ['Gear or bearing noise', 'Ruido engranajes / Ruido rodamientos'],
  ['Breather valve', 'Válvula de respiro'],
  ['Oil leak, output seal', 'Fuga Sello Salida (FSS)'],
  ['Oil leak, input seal', 'Fuga Sello Entrada (FSE)'],
  ['Oil passing to motor', 'Paso Aceite Motor (PAM)'],
  ['Oil leak at plugs', 'Fugas x Tapón (FXT)'],
  ['Oil leak at gaskets and covers', 'Fugas Juntas Tapas'],
  ['Low oil level', 'Nivel de Aceite'],
  ['Oil oxidation or contamination', 'Estado de aceite'],
  ['Operating temperature', 'Temp. operación (ºC)'],
  ['Loose or incorrect anchoring', 'Anclaje (ANC)'],
  ['Belt or chain transmission', 'Transmisión'],
  ['Exterior cleanliness', 'Aseo'],
  ['No abnormal noise.', 'Sin ruido anormal.'],
  ['High-frequency noise (wear or pitting).', 'Ruido de alta frecuencia (desgaste o picaduras).'],
  ['Low-frequency noise (breakage or knocking).', 'Ruido de baja frecuencia (rotura o golpeteo).'],
  ['Breather valve free and working.', 'Válvula de respiro libre y funcionando.'],
  ['Breather valve under compression.', 'Válvula de respiro bajo compresión.'],
  ['Breather valve under compression with oil leak.', 'Válvula de respiro bajo compresión con fuga de aceite.'],
  ['No leak.', 'Sin fuga.'],
  ['Oil top-up needed about every 2 weeks.', 'Se necesita reponer aceite aproximadamente cada 2 semanas.'],
  ['Oil top-up needed more often than every 2 weeks.', 'Se necesita reponer aceite con mayor frecuencia que cada 2 semanas.'],
  ['No oil in the motor.', 'Sin aceite en el motor.'],
  ['Humidity in the stator.', 'Humedad en el estator.'],
  ['Leak at the flanges or terminal box.', 'Fuga en las bridas o la caja de bornes.'],
  ['Plugs dry and tight.', 'Tapones secos y ajustados.'],
  ['Loose plug with dampness.', 'Tapón flojo con humedad.'],
  ['Visible leak at the plug.', 'Fuga visible en el tapón.'],
  ['Gaskets and covers dry.', 'Juntas y tapas secas.'],
  ['Humidity at the gasket or cover.', 'Humedad en la junta o la tapa.'],
  ['Visible leak at the gasket or cover.', 'Fuga visible en la junta o la tapa.'],
  ['Oil level at the middle of the sight glass.', 'Nivel de aceite a la mitad del visor.'],
  ['Top-up of up to 0.3 L needed.', 'Se necesita reponer hasta 0.3 L.'],
  ['Top-up of more than 0.3 L needed.', 'Se necesita reponer más de 0.3 L.'],
  ['Oil clean with normal colour.', 'Aceite limpio con color normal.'],
  ['Oil dark but still viscous.', 'Aceite oscuro pero aún viscoso.'],
  ['Oil has lost viscosity (change interval hours = 0.0005 x operating hours).', 'El aceite perdió viscosidad (intervalo de cambio en horas = 0.0005 x horas de operación).'],
  ['Normal operating temperature.', 'Temperatura de operación normal.'],
  ['Elevated temperature (source text says below 50 C; wording is ambiguous).', 'Temperatura elevada (el texto de origen indica por debajo de 50 C; la redacción es ambigua).'],
  ['High temperature (source text says above 75 C; wording is ambiguous).', 'Temperatura alta (el texto de origen indica por encima de 75 C; la redacción es ambigua).'],
  ['Source wording for the medium/critical thresholds is ambiguous; see docs/SOURCE-FINDINGS.md section 7.', 'La redacción de origen para los umbrales medio/crítico es ambigua; ver docs/SOURCE-FINDINGS.md sección 7.'],
  ['Anchoring firm and correct.', 'Anclaje firme y correcto.'],
  ['Loose cones, bolts or tensioners.', 'Conos, pernos o tensores flojos.'],
  ['Broken cones or shaft wobble.', 'Conos rotos u oscilación del eje.'],
  ['Transmission aligned and correctly tensioned.', 'Transmisión alineada y con tensión correcta.'],
  ['Misalignment or low tension.', 'Desalineación o tensión baja.'],
  ['Vibration and noise.', 'Vibración y ruido.'],
  ['Exterior clean.', 'Exterior limpio.'],
  ['Dry dust on the exterior.', 'Polvo seco en el exterior.'],
  ['Wet dust on the exterior.', 'Polvo húmedo en el exterior.'],
  ['Stable', 'Estable'], ['Warning', 'Advertencia'], ['Preventive', 'Preventivo'], ['Critical', 'Crítico'],
  ['Reference', 'Referencia'], ['Green', 'Verde'], ['Orange', 'Naranja'], ['Red', 'Rojo'],
  ['Ranges taken from the equipment sheets; not validated by maintenance. Informational only.', 'Rangos tomados de las fichas de los equipos; no validados por mantenimiento. Solo informativo.'],
  ['Equipment was off (operating_state=off); only static criteria are meaningful.', 'El equipo estaba apagado (operating_state=off); solo los criterios estáticos son significativos.'],
  ['Equipment was dismounted (operating_state=dismounted); only static criteria are meaningful.', 'El equipo estaba desmontado (operating_state=dismounted); solo los criterios estáticos son significativos.'],
  ['Equipment was disconnected (operating_state=disconnected); only static criteria are meaningful.', 'El equipo estaba desconectado (operating_state=disconnected); solo los criterios estáticos son significativos.'],
  ['Dismounted', 'Desmontado'], ['Disconnected', 'Desconectado'],
])

export function inspectionText(text: string): string {
  const exact = TEXT.get(text)
  if (exact !== undefined) return exact
  let match = /^Not assessed \((not_applicable|not_measured|missing)\); excluded from the sum\.$/.exec(text)
  if (match) return `Sin evaluar (${levelLabel(match[1])}); excluido de la suma.`
  match = /^Invalid level '([^'\r\n]*)'; excluded from the sum, needs review\.$/.exec(text)
  if (match) return `Nivel inválido '${match[1]}'; excluido de la suma, requiere revisión.`
  match = /^Criterion (\w+) has an invalid or out-of-domain level '([^'\r\n]*)'; excluded\.$/.exec(text)
  if (match) return `El criterio ${match[1]} tiene un nivel inválido o fuera de dominio '${match[2]}'; excluido.`
  match = /^Partial coverage: (\d+) of (\d+) criteria scored; the total understates the risk\.$/.exec(text)
  if (match) return `Cobertura parcial: ${match[1]} de ${match[2]} criterios puntuados; el total subestima el riesgo.`
  return text
}

/** Only known generated lines inside notes; raw readings and surrounding prose remain verbatim. */
export function inspectionNotes(text: string): string {
  const quantities: Record<string, string> = { current: 'corriente', voltage: 'voltaje', frequency: 'frecuencia' }
  return text.replace(/^Sheet reading (current|voltage|frequency): reducer ([^\r\n]*); motor ([^\r\n]*)$/gm,
    (_, quantity: string, reducer: string, motor: string) => `Lectura de la ficha de ${quantities[quantity]}: reductor ${reducer}; motor ${motor}`)
}

export function referenceBandLabel(code: string): string {
  const labels = new Map([['green', 'Verde'], ['orange', 'Naranja'], ['red', 'Rojo'], ['not_assessed', 'Sin evaluar']])
  return labels.get(code) ?? code
}
